import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore, type KeyboardEvent, type PointerEvent } from "react";
import { formatTakenDate, seededShuffle, type Photo } from "../../../shared/photos";
import { dataStateOf } from "../../data/queries";
import { Icon } from "../../ui/Icon";
import { EmptyState, StatusBadge } from "../../ui/StatusBadge";
import type { WidgetDefinition, WidgetProps } from "../types";
import { usePhotoLibrary } from "./api";
import { PhotoMarkButtons } from "./PhotoMarks";
import { PhotoGlyph, PhotoImage, useBoxSize, usePageVisible, useStillPhotos, variantFor } from "./PhotoView";
import "./photos.css";

interface PhotoFrameConfig {
  interval: number;
  album: string;
  shuffle: boolean;
  showCaption: boolean;
  showDate: boolean;
}

const FADE_MS = 1400;
const SWIPE_PX = 45;

/** Newest photos first by when they were taken; undated photos last. */
function chronological(list: Photo[]): Photo[] {
  return [...list].sort((a, b) => (b.takenAt ?? "").localeCompare(a.takenAt ?? ""));
}

interface Layer {
  key: number;
  photo: Photo;
  loaded: boolean;
}

/**
 * A tile and its full-screen view are one slideshow: same shuffle, same photo, same pause.
 * `covered` is set while the full-screen view is open, so the tile underneath sits still.
 */
interface Slideshow {
  seed: string;
  photoId: string | null;
  paused: boolean;
  covered: boolean;
}
const slideshows = new Map<string, Slideshow>();
const slideshowListeners = new Set<() => void>();

function slideshowFor(id: string): Slideshow {
  let s = slideshows.get(id);
  if (!s) slideshows.set(id, (s = { seed: Math.random().toString(36).slice(2), photoId: null, paused: false, covered: false }));
  return s;
}

function updateSlideshow(id: string, patch: Partial<Slideshow>) {
  const s = slideshowFor(id);
  if ((Object.keys(patch) as (keyof Slideshow)[]).every((k) => s[k] === patch[k])) return;
  slideshows.set(id, { ...s, ...patch });
  slideshowListeners.forEach((l) => l());
}

function onSlideshowChange(listener: () => void) {
  slideshowListeners.add(listener);
  return () => slideshowListeners.delete(listener);
}

export function PhotoEmpty({ album }: { album?: string }) {
  return (
    <div className="pf-empty">
      <span className="pf-empty-glyph">
        <PhotoGlyph size="2rem" />
      </span>
      <strong>{album ? `No wall photos in “${album}”` : "No family photos on the wall yet"}</strong>
      <span>Photos come from the home photo library once a parent approves folders for the wall (Admin → Photo library). Kinmantle never stores them.</span>
    </div>
  );
}

function PhotoFrame({ instanceId, config, size, editing, fullscreen, openFullscreen }: WidgetProps<PhotoFrameConfig>) {
  const album = config.album.trim();
  const q = usePhotoLibrary(album);
  const state = dataStateOf(q, 90 * 60_000);
  const still = useStillPhotos();
  const visible = usePageVisible();
  const shared = useSyncExternalStore(onSlideshowChange, () => slideshowFor(instanceId));
  const { seed, paused } = shared;
  const setPaused = (p: boolean) => updateSlideshow(instanceId, { paused: p });
  // The tile under an open full-screen view neither advances nor loads photos.
  const covered = shared.covered && !fullscreen;
  const [boxRef, box] = useBoxSize<HTMLDivElement>();

  useEffect(() => {
    if (!fullscreen) return;
    updateSlideshow(instanceId, { covered: true });
    return () => updateSlideshow(instanceId, { covered: false });
  }, [fullscreen, instanceId]);

  const order = useMemo(() => {
    const list = q.data ?? [];
    return config.shuffle ? seededShuffle(chronological(list), seed) : chronological(list);
  }, [q.data, config.shuffle, seed]);

  const [layers, setLayers] = useState<Layer[]>([]);
  const [failures, setFailures] = useState(0);
  const keyRef = useRef(0);
  const current = layers[layers.length - 1];
  const index = current ? order.findIndex((p) => p.id === current.photo.id) : -1;

  const show = useCallback(
    (photo: Photo) => {
      updateSlideshow(instanceId, { photoId: photo.id });
      setLayers((ls) => {
        const top = ls[ls.length - 1];
        if (top?.photo.id === photo.id) return ls;
        keyRef.current += 1;
        // Keep at most the outgoing photo underneath while the new one fades in.
        return [...ls.slice(-1), { key: keyRef.current, photo, loaded: false }];
      });
    },
    [instanceId],
  );

  const step = useCallback(
    (dir: 1 | -1) => {
      if (!order.length) return;
      const from = index < 0 ? (dir === 1 ? -1 : 0) : index;
      show(order[(from + dir + order.length) % order.length]!);
    },
    [order, index, show],
  );

  // Start where the slideshow left off (or from the top if that photo is gone).
  useEffect(() => {
    if (!order.length) {
      setLayers([]);
      return;
    }
    if (index < 0) show(order.find((p) => p.id === slideshowFor(instanceId).photoId) ?? order[0]!);
  }, [order, index, show, instanceId]);

  // Follow the photo the other view of this slideshow moved to (once uncovered, for the tile).
  const currentId = current?.photo.id;
  useEffect(() => {
    if (covered || !shared.photoId || !currentId || shared.photoId === currentId) return;
    const photo = order.find((p) => p.id === shared.photoId);
    if (photo) show(photo);
  }, [covered, shared.photoId, currentId, order, show]);

  // Advance on the configured interval; never while paused, editing, covered or hidden.
  const running = !paused && !editing && !covered && visible && order.length > 1 && failures < Math.min(order.length, 6);
  useEffect(() => {
    if (!running || !current) return;
    const t = window.setTimeout(() => step(1), config.interval * 1000);
    return () => clearTimeout(t);
  }, [running, current?.key, config.interval, step]); // eslint-disable-line react-hooks/exhaustive-deps

  // Drop the outgoing layer once the crossfade has finished.
  useEffect(() => {
    if (layers.length < 2 || !current?.loaded) return;
    const t = window.setTimeout(() => setLayers((ls) => ls.slice(-1)), still ? 450 : FADE_MS + 100);
    return () => clearTimeout(t);
  }, [layers.length, current?.loaded, current?.key, still]);

  // Preload only the next photo, at the size it will be shown.
  const next = order.length > 1 && index >= 0 ? order[(index + 1) % order.length] : undefined;
  const nextSrc = next && box.w ? (variantFor(next, box, !still) === "full" ? next.url : next.thumbUrl) : undefined;
  useEffect(() => {
    if (!nextSrc || editing || covered) return;
    const img = new Image();
    img.decoding = "async";
    img.src = nextSrc;
    return () => {
      img.src = "";
    };
  }, [nextSrc, editing, covered]);

  // A photo that won't load is skipped after a moment, up to a few in a row.
  const onLoaded = (key: number) => {
    setFailures(0);
    setLayers((ls) => ls.map((l) => (l.key === key ? { ...l, loaded: true } : l)));
  };
  const onFailed = () => {
    setFailures((f) => f + 1);
    window.setTimeout(() => step(1), 1200);
  };
  // Try again when the list refreshes (e.g. back online).
  useEffect(() => setFailures(0), [q.dataUpdatedAt]);

  // Gestures: tap pauses/resumes, horizontal swipe changes photo.
  const down = useRef<{ x: number; y: number } | null>(null);
  const onPointerDown = (e: PointerEvent) => {
    if ((e.target as HTMLElement).closest("button, a")) return;
    down.current = { x: e.clientX, y: e.clientY };
  };
  const onPointerUp = (e: PointerEvent) => {
    const d = down.current;
    down.current = null;
    if (!d || editing) return;
    const dx = e.clientX - d.x;
    const dy = e.clientY - d.y;
    if (Math.abs(dx) > SWIPE_PX && Math.abs(dx) > Math.abs(dy) * 1.2) step(dx < 0 ? 1 : -1);
    else if (Math.abs(dx) < 12 && Math.abs(dy) < 12) setPaused(!paused);
  };
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === "ArrowRight") step(1);
    else if (e.key === "ArrowLeft") step(-1);
    else if (e.key === " " || e.key === "Enter") {
      e.preventDefault();
      setPaused(!paused);
    }
  };

  if (!q.data) {
    if (state === "loading") return <div className="widget-loading" />;
    return (
      <EmptyState
        icon={state === "offline" ? "wifiOff" : "alert"}
        title="Photos are unavailable right now"
        detail={state === "offline" ? "This screen is offline. Photos come back when the connection does." : "Kinmantle couldn’t load the photo list. Retrying automatically."}
      />
    );
  }
  if (!order.length) return <PhotoEmpty album={album || undefined} />;

  const shown = current?.photo;
  const caption = config.showCaption ? shown?.caption : null;
  const date = config.showDate ? formatTakenDate(shown?.takenAt ?? null) : null;
  const allFailed = failures >= Math.min(order.length, 6);
  const motionMs = config.interval * 1000 + FADE_MS * 2;

  return (
    <div
      ref={boxRef}
      className={`pf ${paused ? "is-paused" : ""} ${still ? "is-still" : ""} ${size.variant === "compact" ? "is-compact" : ""}`}
      role="region"
      aria-roledescription="slideshow"
      aria-label={album ? `Photos: ${album}` : "Family photos"}
      tabIndex={editing ? -1 : 0}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
      onPointerCancel={() => (down.current = null)}
      onKeyDown={onKeyDown}
    >
      {layers.map((l) => {
        const isTop = l.key === current?.key;
        return (
          <div key={l.key} className={`pf-layer ${isTop ? (l.loaded ? "on" : "") : "out"}`} style={{ ["--fade" as string]: `${still ? 400 : FADE_MS}ms` }}>
            <PhotoImage
              photo={l.photo}
              box={box}
              still={still}
              motion
              durationMs={motionMs}
              onLoad={() => onLoaded(l.key)}
              onError={isTop ? onFailed : undefined}
            />
          </div>
        );
      })}

      {(caption || date) && !allFailed && (
        <div className="pf-caption" aria-live="polite">
          {caption && <strong>{caption}</strong>}
          {date && <span>{date}</span>}
        </div>
      )}

      {allFailed && (
        <div className="pf-notice">
          <Icon name="wifiOff" size="1.1rem" />
          Photos can’t be loaded right now. Retrying automatically.
        </div>
      )}

      {order.length > 1 && !editing && (
        <>
          <button className="pf-nav prev" onClick={() => step(-1)} aria-label="Previous photo">
            <Icon name="chevronLeft" />
          </button>
          <button className="pf-nav next" onClick={() => step(1)} aria-label="Next photo">
            <Icon name="chevronRight" />
          </button>
        </>
      )}

      <div className="pf-tools">
        {paused && (
          <button className="pf-chip" onClick={() => setPaused(false)} aria-label="Resume slideshow">
            <Icon name="play" size="1rem" />
            Paused
          </button>
        )}
        {shown && !editing && <PhotoMarkButtons photo={shown} />}
        {!fullscreen && !editing && (
          <button className="pf-round" onClick={openFullscreen} aria-label="Show photos full-screen">
            <Icon name="expand" size="1.15rem" />
          </button>
        )}
      </div>

      {order.length > 1 && index >= 0 && size.variant !== "compact" && (
        <span className="pf-count" aria-hidden="true">
          {index + 1} / {order.length}
        </span>
      )}
      <StatusBadge state={state} updatedAt={q.dataUpdatedAt} />
    </div>
  );
}

export const photoFrameWidget: WidgetDefinition<PhotoFrameConfig> = {
  type: "photo-frame",
  version: 1,
  name: "Photo frame",
  description: "A slideshow of family photos with gentle motion. Tap to pause, swipe for the next one.",
  icon: "layout",
  category: "household",
  defaultSize: { w: 5, h: 6 },
  minSize: { w: 2, h: 2 },
  stackHeight: 380,
  defaults: { interval: 30, album: "", shuffle: true, showCaption: true, showDate: true },
  settings: [
    { key: "interval", label: "Seconds per photo", type: "number", min: 10, max: 300, step: 5 },
    { key: "album", label: "Album", type: "text", maxLength: 40, placeholder: "All photos", help: "A folder such as Travel, or a tag such as dog. Leave blank for every wall photo." },
    { key: "shuffle", label: "Shuffle", type: "toggle" },
    { key: "showCaption", label: "Show captions", type: "toggle" },
    { key: "showDate", label: "Show date taken", type: "toggle" },
  ],
  provider: "photos",
  capabilities: { requiresAuth: true },
  supportsFullscreen: true,
  chrome: "card",
  Component: PhotoFrame,
};
